import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Link,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { alpha } from '@mui/material/styles';
import { providerApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Formats a date string to a readable locale date string.
 */
function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  } catch {
    return iso;
  }
}

/**
 * Formats price in cents to a currency string using symbol from the product.
 */
function fmtPrice(cents, currency) {
  if (cents == null) return '';
  const symbol = currency === 'ILS' ? '₪' : (currency || '');
  return `${symbol}${(cents / 100).toFixed(0)}`;
}

/** Stable reason codes the backend puts in the error body (spec §4.9). */
const SUBSCRIPTION_ERROR_CODES = new Set([
  'PRO_DISABLED', 'CHECKOUT_RESTARTING', 'CHECKOUT_NEEDS_REVIEW', 'ACCOUNT_DELETING', 'PAYPAL_UNAVAILABLE', 'ALREADY_SUBSCRIBED',
]);

function errorCodeOf(e) {
  return e?.response?.data?.message || e?.response?.data?.error || '';
}

/** Copy for a failed subscribe/create call. `sub` (may be null) supplies the period end for ALREADY_SUBSCRIBED. */
function subscriptionErrorCopy(t, e, sub) {
  switch (errorCodeOf(e)) {
    case 'PRO_DISABLED': return t('subscription.errProDisabled');
    case 'CHECKOUT_RESTARTING': return t('subscription.errCheckoutRestarting');
    case 'CHECKOUT_NEEDS_REVIEW': return t('subscription.errCheckoutNeedsReview');
    case 'ACCOUNT_DELETING': return t('subscription.errAccountDeleting');
    case 'PAYPAL_UNAVAILABLE': return t('subscription.errPaypalUnavailable');
    case 'ALREADY_SUBSCRIBED': return sub?.currentPeriodEnd
      ? t('subscription.errAlreadySubscribedCanceled', { date: fmtDate(sub.currentPeriodEnd) })
      : t('subscription.errAlreadySubscribed');
    default: return e?.response?.status === 503 ? t('subscription.errPaypalUnavailable') : t('subscription.startFailed');
  }
}

/**
 * Subscription section shown inside UpgradePage when the provider is already Pro.
 * States (spec §5): granted (ADMIN row), paid ACTIVE, GRACE, CANCELED-with-access, ended,
 * and a PENDING checkout with no row yet. Error codes come from the `reason` of the response.
 */
function SubscriptionSection({ t, subProduct }) {
  const { refreshProviderData } = useAuth();
  const [subView, setSubView] = useState(null); // { subscription, checkout } | null
  const [subLoading, setSubLoading] = useState(true);
  const [subError, setSubError] = useState('');
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelPending, setCancelPending] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadSub = providerApi.getProSubscription()
      .then((data) => { if (!cancelled) setSubView(data || null); })
      .catch((e) => {
        // 404 = nothing on file — a valid "no subscription" state.
        if (e?.response?.status === 404) {
          if (!cancelled) setSubView(null);
        } else {
          if (!cancelled) setSubError(t('subscription.loadFailed'));
        }
      })
      .finally(() => { if (!cancelled) setSubLoading(false); });

    return () => { cancelled = true; void loadSub; };
  }, [t]);

  const sub = subView?.subscription || null;
  const checkoutState = subView?.checkout?.state || null;
  const isGrant = sub?.channel === 'ADMIN' && sub?.status === 'ACTIVE';
  const isActive = sub?.channel === 'PAYPAL' && sub?.status === 'ACTIVE';
  const isGrace = sub?.status === 'GRACE';
  const isCanceledWithAccess = sub?.status === 'CANCELED' && sub?.currentPeriodEnd && new Date(sub.currentPeriodEnd) > new Date();
  const isEnded = sub && !isGrant && !isActive && !isGrace && !isCanceledWithAccess;
  const canSubscribe = !sub || isGrant || isEnded;

  const errorCopyFor = (e) => subscriptionErrorCopy(t, e, sub);

  const handleSubscribe = async () => {
    setSubscribeLoading(true); setActionError('');
    try {
      const data = await providerApi.createProSubscription();
      // Redirect in the same tab so PayPal returns to /paypal-callback?type=subscription.
      if (data?.approvalUrl) { window.location.assign(data.approvalUrl); return; }
      // Recovered an already-active checkout (spec §4.3 resolve → Activated).
      await refreshProviderData();
      window.location.replace('/dashboard?tab=upgrade');
    } catch (e) {
      setActionError(errorCopyFor(e));
      setSubscribeLoading(false);
    }
  };

  const handleCancel = async () => {
    setCancelLoading(true); setActionError('');
    try {
      const res = await providerApi.cancelProSubscription();
      if (res?.status === 202) setCancelPending(true);
      else setSubView((prev) => (prev ? { ...prev, subscription: { ...prev.subscription, status: 'CANCELED' } } : prev));
    } catch (e) {
      setActionError(t('subscription.cancelFailed'));
    } finally { setCancelLoading(false); }
  };

  if (subLoading) {
    return <CircularProgress size={24} />;
  }

  if (subError) {
    return <Alert severity="error">{subError}</Alert>;
  }

  // Price display: prefer server product; fall back to nothing (never hardcode).
  const hasDiscount = subProduct && subProduct.listPriceCents > subProduct.displayPriceCents;
  const hasPromoCycles = subProduct && subProduct.promoCycles;

  const renderPriceLabel = () => {
    if (!subProduct) return null;
    const display = fmtPrice(subProduct.displayPriceCents, subProduct.currency);
    const list = fmtPrice(subProduct.listPriceCents, subProduct.currency);
    if (hasPromoCycles) {
      // Full disclosure required for intro offers.
      return (
        <Typography variant="body2" color="text.secondary">
          {t('subscription.introPricing', {
            promoPrice: display,
            promoCycles: subProduct.promoCycles,
            regularPrice: list,
          })}
        </Typography>
      );
    }
    if (hasDiscount) {
      return (
        <Typography variant="body2" color="text.secondary">
          <Box
            component="span"
            sx={{ textDecoration: 'line-through', color: 'text.disabled', mr: 0.5 }}
          >
            {list}/month
          </Box>
          {display}/month
        </Typography>
      );
    }
    return (
      <Typography variant="body2" color="text.secondary">
        {display}/month
      </Typography>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" fontWeight={700}>{t('subscription.proTitle')}</Typography>
        {isActive && <Chip label={t('subscription.statusActive')} size="small" color="success" sx={{ fontSize: '0.7rem' }} />}
        {isGrace && <Chip label={t('subscription.statusGrace')} size="small" color="warning" sx={{ fontSize: '0.7rem' }} />}
        {isEnded && <Chip label={t('subscription.statusExpired')} size="small" color="default" sx={{ fontSize: '0.7rem' }} />}
      </Box>

      {isGrant && (
        <>
          <Typography variant="body2" color="text.secondary">{t('subscription.grantedUntil', { date: fmtDate(sub.currentPeriodEnd) })}</Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{t('subscription.grantedSubscribeHint')}</Typography>
        </>
      )}
      {isActive && sub.currentPeriodEnd && <Typography variant="body2" color="text.secondary">{t('subscription.renewsOn', { date: fmtDate(sub.currentPeriodEnd) })}</Typography>}
      {isGrace && (
        <>
          <Typography variant="body2" color="text.secondary">{t('subscription.graceUntil', { date: fmtDate(sub.graceDeadlineAt) })}</Typography>
          <Link href="https://www.paypal.com/myaccount/autopay/" target="_blank" rel="noopener" variant="body2">{t('subscription.fixPaymentLink')}</Link>
        </>
      )}
      {isCanceledWithAccess && <Typography variant="body2" color="text.secondary">{t('subscription.errAlreadySubscribedCanceled', { date: fmtDate(sub.currentPeriodEnd) })}</Typography>}
      {checkoutState === 'PENDING' && !sub && <Typography variant="body2" color="text.secondary">{t('paypal.subscriptionPendingBody')}</Typography>}

      {canSubscribe && renderPriceLabel()}
      {actionError && <Alert severity="error" sx={{ mt: 1.5, mb: 1 }}>{actionError}</Alert>}
      {cancelPending && <Alert severity="info" sx={{ mt: 1.5, mb: 1 }}>{t('subscription.cancelInProgress')}</Alert>}

      {canSubscribe && (
        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={handleSubscribe} disabled={subscribeLoading} startIcon={subscribeLoading ? <CircularProgress size={18} color="inherit" /> : null}>
            {isEnded ? t('subscription.resubscribeButton') : t('subscription.subscribeButton')}
          </Button>
        </Box>
      )}
      {(isActive || isGrace) && !cancelPending && (
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" color="error" onClick={handleCancel} disabled={cancelLoading} size="small" startIcon={cancelLoading ? <CircularProgress size={16} color="inherit" /> : null}>
            {t('subscription.cancelButton')}
          </Button>
        </Box>
      )}
    </Box>
  );
}

// The version of the Pro-terms CONTENT this build renders (upgrade.termsBody).
// If the server activates a newer version, block acceptance until a deploy
// ships matching content — never record acceptance of terms we cannot show.
const BUNDLED_TERMS_CONTENT_VERSION = '1.0';

function PlanCard({ title, priceAmount, priceUnit, items, highlighted, footer, t }) {
  return (
    <Card
      elevation={highlighted ? 8 : 1}
      sx={{
        flex: 1,
        minWidth: 260,
        maxWidth: 340,
        borderRadius: 3,
        overflow: 'hidden',
        border: '2px solid',
        borderColor: highlighted ? 'primary.main' : 'divider',
        transform: highlighted ? { sm: 'translateY(-10px)' } : 'none',
        transition: 'transform 0.2s, box-shadow 0.2s',
      }}
    >
      {/* Colored header band on the highlighted (Pro) card */}
      <Box
        sx={{
          background: highlighted
            ? 'linear-gradient(135deg, #6D28D9 0%, #8B5CF6 100%)'
            : alpha('#64748B', 0.06),
          py: 1.5,
          textAlign: 'center',
        }}
      >
        <Typography
          sx={{
            color: highlighted ? '#fff' : 'text.disabled',
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}
        >
          {highlighted ? t('upgrade.recommended') : t('upgrade.basicTitle')}
        </Typography>
      </Box>

      <CardContent sx={{ p: 3.5 }}>
        {/* Plan name */}
        <Typography
          variant="overline"
          sx={{
            color: highlighted ? 'primary.main' : 'text.secondary',
            fontWeight: 700,
            letterSpacing: '0.08em',
            display: 'block',
            mb: 1,
          }}
        >
          {title}
        </Typography>

        {/* Price */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 3 }}>
          <Typography
            variant="h3"
            fontWeight={800}
            sx={{
              color: highlighted ? 'primary.main' : 'text.primary',
              lineHeight: 1,
              fontSize: { xs: '2.2rem', sm: '2.6rem' },
            }}
          >
            {priceAmount}
          </Typography>
          {priceUnit && (
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {priceUnit}
            </Typography>
          )}
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* Feature list */}
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          {items.map((item) => (
            <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <CheckCircleIcon
                sx={{ fontSize: 18, color: highlighted ? 'success.main' : 'success.light', flexShrink: 0 }}
              />
              <Typography variant="body2" fontWeight={highlighted ? 500 : 400}>
                {item}
              </Typography>
            </Box>
          ))}
        </Stack>

        {footer}
      </CardContent>
    </Card>
  );
}

/**
 * Self-serve Pro upgrade wizard: plan comparison → accept terms → activate.
 * After activation the SubscriptionSection handles PayPal recurring billing (₪49/mo).
 * PayPal payout onboarding (for receiving customer payments) is a separate optional
 * step surfaced via the PayoutOnboardingBanner on the main dashboard.
 */
export default function UpgradePage({ providerData }) {
  const { t } = useTranslation();
  const { refreshProviderData } = useAuth();
  const [step, setStep] = useState(0);
  const [termsVersion, setTermsVersion] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [activated, setActivated] = useState(Boolean(providerData?.isProPlan));
  // The ONE subscription product checkout will bill (server-resolved, promo included). Drives the
  // plan card price, the billing disclosure and the Pro section; copy constants are the fallback
  // only while it is unavailable.
  const [subProduct, setSubProduct] = useState(null);
  useEffect(() => {
    let cancelled = false;
    providerApi.getBillingProducts({ kind: 'SUBSCRIPTION' })
      .then((data) => { if (!cancelled) setSubProduct(Array.isArray(data) && data.length ? data[0] : null); })
      .catch(() => { /* pricing unavailable — fall back to copy */ });
    return () => { cancelled = true; };
  }, []);
  const proPriceAmount = subProduct ? fmtPrice(subProduct.displayPriceCents, subProduct.currency) : t('upgrade.proPriceAmount');
  const billingNote = (() => {
    if (!subProduct) return t('upgrade.billingNote');
    const display = fmtPrice(subProduct.displayPriceCents, subProduct.currency);
    const list = fmtPrice(subProduct.listPriceCents, subProduct.currency);
    if (subProduct.promoCycles) {
      return t('upgrade.billingNotePromo', { promoPrice: display, promoCycles: subProduct.promoCycles, regularPrice: list });
    }
    return t('upgrade.billingNotePrice', { price: display });
  })();

  const steps = [
    t('upgrade.stepPlans'),
    t('upgrade.stepTerms'),
    t('upgrade.stepPayment'),
    t('upgrade.stepComplete'),
  ];

  const loadTermsVersion = useCallback(async () => {
    try {
      const data = await providerApi.getProTermsVersion();
      setTermsVersion(data.version);
    } catch (e) {
      setError(t('upgrade.termsLoadFailed'));
    }
  }, [t]);

  useEffect(() => {
    if (!activated) loadTermsVersion();
  }, [activated, loadTermsVersion]);

  const activate = async () => {
    setBusy(true);
    setError('');
    try {
      await providerApi.registerAsPro(termsVersion);
      const data = await providerApi.createProSubscription();
      if (data?.approvalUrl) {
        window.location.assign(data.approvalUrl);
        return; // Page navigates away — no setBusy(false) or setActivated(true) needed
      }
      // 200 without an approval URL: an earlier checkout was recovered as already active
      // (spec §4.3 resolve → Activated). Show the Pro state instead of navigating to null.
      await refreshProviderData();
      setActivated(true);
      setBusy(false);
    } catch (e) {
      if (errorCodeOf(e) === 'ALREADY_SUBSCRIBED') {
        // A live PayPal subscription already exists: the Pro section shows its real state.
        await refreshProviderData();
        setActivated(true);
      } else if (SUBSCRIPTION_ERROR_CODES.has(errorCodeOf(e)) || e.response?.status === 503) {
        // Checkout refused for a stated reason (spec §4.9) — not an onboarding problem.
        setError(subscriptionErrorCopy(t, e, null));
      } else if (e.response?.status === 409) {
        setError(t('upgrade.activation409'));
      } else if (e.response?.status === 400) {
        setError(t('upgrade.activation400'));
        await loadTermsVersion();
        setStep(1);
      } else {
        setError(t('subscription.startFailed'));
      }
      setBusy(false);
    }
  };

  // activeStep drives the stepper: 0-1 for the wizard, 3 (last) when fully activated.
  // Steps 0-2 get the MUI "completed" treatment (checkmark) automatically when activeStep=3.
  const activeStep = activated ? 3 : step;

  return (
    <Box>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      {activated && (
        <Stack spacing={3}>
          <Card>
            <CardContent sx={{ p: 5, textAlign: 'center' }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 1 }} />
              <Typography variant="h5" fontWeight={700} gutterBottom>
                {t('upgrade.successTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('upgrade.successBody')}
              </Typography>
            </CardContent>
          </Card>

          {/* Subscription billing — the primary next step after activation */}
          <Card elevation={2} sx={{ border: '2px solid', borderColor: 'primary.light', borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WorkspacePremiumIcon color="primary" sx={{ fontSize: 22 }} />
                <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                  {t('subscription.sectionTitle')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('subscription.sectionBody')}
              </Typography>
              <SubscriptionSection t={t} subProduct={subProduct} />
            </CardContent>
          </Card>
        </Stack>
      )}

      {!activated && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!activated && step === 0 && (
        <Box>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              {t('upgrade.chooseTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {billingNote}
            </Typography>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={3}
            justifyContent="center"
            alignItems={{ xs: 'center', sm: 'flex-start' }}
            sx={{ mb: 4, pt: { sm: 1.5 } }}
          >
            <PlanCard
              t={t}
              title={t('upgrade.basicTitle')}
              priceAmount={t('upgrade.basicPrice')}
              priceUnit=""
              items={[
                t('upgrade.basicFeatureProfile'),
                t('upgrade.basicFeatureServices'),
                t('upgrade.basicFeatureContacts'),
                t('upgrade.basicFeatureLeads'),
                t('upgrade.basicFeatureVerified'),
              ]}
              footer={
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                  <Chip
                    label={t('upgrade.currentPlan')}
                    variant="outlined"
                    size="small"
                    sx={{ color: 'text.disabled', borderColor: 'divider' }}
                  />
                </Box>
              }
            />
            <PlanCard
              t={t}
              highlighted
              title={t('upgrade.proTitle')}
              priceAmount={proPriceAmount}
              priceUnit={t('upgrade.proPriceUnit')}
              items={[
                t('upgrade.proFeatureEverything'),
                t('upgrade.proFeatureBookings'),
                t('upgrade.proFeatureAvailability'),
                t('upgrade.proFeatureAnalytics'),
                t('upgrade.proFeatureReviews'),
              ]}
              footer={
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{ mt: 1, py: 1.5, fontWeight: 700, fontSize: '1rem' }}
                  onClick={() => setStep(1)}
                >
                  {t('upgrade.startButton')}
                </Button>
              }
            />
          </Stack>
        </Box>
      )}

      {!activated && step === 1 && (
        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>{t('upgrade.termsTitle')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('upgrade.termsBody')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              {termsVersion
                ? t('upgrade.termsVersion', { version: termsVersion })
                : t('common.loading')}
            </Typography>
            {termsVersion && termsVersion !== BUNDLED_TERMS_CONTENT_VERSION ? (
              <Alert severity="warning">{t('upgrade.termsContentOutdated')}</Alert>
            ) : (
              <FormControlLabel
                control={
                  <Checkbox checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
                }
                label={t('upgrade.termsAccept')}
              />
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button onClick={() => setStep(0)}>{t('common.back')}</Button>
              <Button
                variant="contained"
                disabled={!termsAccepted || !termsVersion || termsVersion !== BUNDLED_TERMS_CONTENT_VERSION || busy}
                onClick={activate}
                startIcon={busy ? <CircularProgress size={18} color="inherit" /> : null}
              >
                {t('upgrade.activateButton')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

    </Box>
  );
}
